// frontend/src/services/createOvertime.service.ts - MULTI-PROJECT + NIGHT SHIFT + EDIT AFTER APPROVAL SUPPORT
import {
  collection,
  doc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  getDoc,
  setDoc,
  addDoc,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebase';
import { FirebaseError } from 'firebase/app';

export interface Employee {
  id: string;
  name: string;
  employeeName?: string;
  employeePin?: string;
  designation: string;
  department: string;
  employeeNumber?: string;
  hasOvertime?: boolean;
  overtime?: string;
  pin?: string;
}

export interface Approver {
  id: string;
  name: string;
  designation: string;
  department: string;
  source: string;
  priority: number;
}

// Project structure for multi-project support
export interface OvertimeProject {
  id: string;
  projectName: string;
  projectCode: string;
  startTime: Date;
  endTime: Date;
  duration: number;
  overtimeDate: Date;
  isNightShift?: boolean;  // Night shift flag (crosses midnight)
  hasLongShift?: boolean;  // Long shift flag
}

// Request data now supports multiple projects
export interface OvertimeRequestData {
  projects: OvertimeProject[];
  employeeIds: string[];
  requesterId: string;
  requesterName: string;
  selectedApproverId: string;
  selectedApproverName: string;
  overtimeDate: Date;
  isNightShift?: boolean;
}

export interface OvertimeRequestResult {
  success: boolean;
  requestId?: string;
  message: string;
}

class CreateOvertimeService {
  private cache = new Map<string, { data: any; timestamp: number }>();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  private getFromCache<T>(key: string): T | null {
    const cached = this.cache.get(key);
    if (cached && (Date.now() - cached.timestamp) < this.CACHE_TTL) {
      console.log('Using cached data for:', key);
      return cached.data;
    }
    this.cache.delete(key);
    return null;
  }

  private setCache(key: string, data: any): void {
    this.cache.set(key, { data, timestamp: Date.now() });
  }

  /**
   * Get all available overtime approvers
   */
  async getAvailableApprovers(): Promise<Approver[]> {
    try {
      console.log('Fetching all available overtime approvers...');
      
      const cacheKey = 'available_approvers';
      const cached = this.getFromCache<Approver[]>(cacheKey);
      if (cached) return cached;

      let approvers: Approver[] = [];
      const addedApprovers = new Set<string>();

      // Method 1: overtime_approvers collection
      try {
        const approversSnapshot = await getDocs(
          query(
            collection(db, 'overtime_approvers'),
            where('isActive', '==', true)
          )
        );

        for (const doc of approversSnapshot.docs) {
          const data = doc.data();
          const approverId = data.approverId || doc.id;

          if (!addedApprovers.has(approverId)) {
            approvers.push({
              id: approverId,
              name: data.approverName || 'Unknown Approver',
              designation: data.designation || 'Overtime Approver',
              department: data.department || 'Management',
              source: 'overtime_approvers',
              priority: 1,
            });
            addedApprovers.add(approverId);
          }
        }
      } catch (error) {
        console.error('Error loading from overtime_approvers:', error);
      }

      // Method 2: MasterSheet
      try {
        const masterSheetSnapshot = await getDocs(
          query(
            collection(db, 'MasterSheet', 'Employee-Data', 'employees'),
            where('hasOvertimeApprovalAccess', '==', true)
          )
        );

        for (const doc of masterSheetSnapshot.docs) {
          const data = doc.data();
          const approverId = doc.id;

          if (!addedApprovers.has(approverId)) {
            approvers.push({
              id: approverId,
              name: data.employeeName || data.name || 'Unknown',
              designation: data.designation || 'Manager',
              department: data.department || 'Unknown Department',
              source: 'mastersheet',
              priority: 2,
            });
            addedApprovers.add(approverId);
          }
        }
      } catch (error) {
        console.error('Error loading from MasterSheet:', error);
      }

      // Sort by priority
      approvers.sort((a, b) => {
        const priorityCompare = a.priority - b.priority;
        if (priorityCompare !== 0) return priorityCompare;
        return a.name.localeCompare(b.name);
      });

      // Fallback
      if (approvers.length === 0) {
        console.warn('No approvers found! Adding fallback');
        approvers.push({
          id: 'EMP1289',
          name: 'Default Approver',
          designation: 'System Approver',
          department: 'Administration',
          source: 'fallback',
          priority: 999,
        });
      }

      this.setCache(cacheKey, approvers);
      console.log(`Total unique approvers found: ${approvers.length}`);

      return approvers;
    } catch (error) {
      console.error('Error fetching available approvers:', error);
      return [{
        id: 'EMP1289',
        name: 'Default Approver',
        designation: 'System Approver',
        department: 'Administration',
        source: 'fallback',
        priority: 999,
      }];
    }
  }

  /**
   * Get all eligible employees for overtime
   */
  async getEligibleEmployees(): Promise<Employee[]> {
    try {
      console.log('🔄 Fetching employees eligible for overtime...');

      const cacheKey = 'eligible_employees';
      const cached = this.getFromCache<Employee[]>(cacheKey);
      if (cached) return cached;

      const uniqueEmployees = new Set<string>();
      const employees: Employee[] = [];

      // Method 1: MasterSheet
      try {
        const masterSheetSnapshot = await getDocs(
          query(
            collection(db, 'MasterSheet', 'Employee-Data', 'employees'),
            where('hasOvertime', '==', true)
          )
        );

        for (const doc of masterSheetSnapshot.docs) {
          if (!uniqueEmployees.has(doc.id)) {
            const data = doc.data();
            employees.push({
              id: doc.id,
              name: data.employeeName || 'Unknown',
              designation: data.designation || 'No designation',
              department: data.department || 'No department',
              employeeNumber: data.employeeNumber || '',
              hasOvertime: true,
              overtime: 'Yes',
              pin: data.pin || data.employeeNumber || doc.id.replace(/^EMP/i, '')
            });
            uniqueEmployees.add(doc.id);
          }
        }
      } catch (error) {
        console.error('Error loading from MasterSheet:', error);
      }

      // Method 2: employees collection fallback
      try {
        const employeesSnapshot = await getDocs(
          query(
            collection(db, 'employees'),
            where('hasOvertime', '==', true)
          )
        );

        for (const doc of employeesSnapshot.docs) {
          if (!uniqueEmployees.has(doc.id)) {
            const data = doc.data();
            employees.push({
              id: doc.id,
              name: data.name || data.employeeName || 'Unknown',
              designation: data.designation || 'No designation',
              department: data.department || 'No department',
              employeeNumber: data.employeeNumber || '',
              hasOvertime: true,
              overtime: 'Yes',
              pin: data.pin || data.employeeNumber || doc.id.replace(/^EMP/i, '')
            });
            uniqueEmployees.add(doc.id);
          }
        }
      } catch (error) {
        console.error('Error loading from employees:', error);
      }

      employees.sort((a, b) => a.name.localeCompare(b.name));

      this.setCache(cacheKey, employees);
      console.log(`✅ Found ${employees.length} employees eligible for overtime`);

      return employees;
    } catch (error) {
      console.error('Error fetching eligible employees:', error);
      throw new Error(`Failed to fetch eligible employees: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get employees for specific date
   */
  async getEligibleEmployeesForDate(overtimeDate: Date): Promise<Employee[]> {
    console.log('📋 Fetching employees for date:', overtimeDate.toISOString().split('T')[0]);
    return this.getEligibleEmployees();
  }

  /**
   * Search employee by PIN
   */
  async searchEmployeeByPinForDate(
    pin: string,
    overtimeDate: Date,
    existingEmployeeIds: string[] = []
  ): Promise<Employee[]> {
    try {
      console.log(`🔍 Searching employee by PIN: ${pin}`);

      const allEmployees = await this.getEligibleEmployees();
      const availableEmployees = allEmployees.filter(emp =>
        !existingEmployeeIds.includes(emp.id)
      );

      const searchResults = availableEmployees.filter(emp => {
        const empPin = emp.employeePin || emp.pin || emp.employeeNumber || '';
        const empName = emp.employeeName?.toLowerCase() || '';
        const searchTerm = pin.toLowerCase();

        return (
          empPin.includes(searchTerm) ||
          empName.includes(searchTerm)
        );
      });

      console.log(`✅ Found ${searchResults.length} matching employees`);
      return searchResults;

    } catch (error) {
      console.error('❌ Error searching employee by PIN:', error);
      throw error;
    }
  }

  /**
   * Calculate next validation time (next day at 6 AM)
   */
  private getNextValidationTime(overtimeDate: Date): Date {
    const validationDate = new Date(overtimeDate);
    validationDate.setDate(validationDate.getDate() + 1);
    validationDate.setHours(6, 0, 0, 0);
    return validationDate;
  }

  /**
   * 🆕 HELPER: Detect if this is a night shift (crosses midnight)
   */
  isNightShiftTime(startTime: string, endTime: string): boolean {
    if (!startTime || !endTime) return false;
    
    const [startHour] = startTime.split(':').map(Number);
    const [endHour] = endTime.split(':').map(Number);
    
    // If end hour is less than start hour, it's next day
    // Also check for edge cases like 23:00 to 01:00
    // Or if start is in evening (18:00+) and end is in morning (before 12:00)
    return endHour < startHour || (startHour >= 18 && endHour < 12);
  }

  /**
   * 🆕 HELPER: Calculate duration properly (supports cross-midnight)
   */
  calculateDuration(startTime: string, endTime: string, isNightShift: boolean): number {
    if (!startTime || !endTime) return 0;
    
    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);
    
    let startMinutes = startHour * 60 + startMinute;
    let endMinutes = endHour * 60 + endMinute;
    
    // If night shift, add 24 hours to end time
    if (isNightShift) {
      endMinutes += 24 * 60; // Add 24 hours in minutes
    }
    
    return Math.max(0, (endMinutes - startMinutes) / 60);
  }

  /**
   * Validate projects for conflicts
   */
  validateProjects(projects: OvertimeProject[]): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (projects.length === 0) {
      errors.push('At least one project is required');
      return { valid: false, errors };
    }

    // Validate each project
    projects.forEach((project, index) => {
      if (!project.projectName?.trim()) {
        errors.push(`Project ${index + 1}: Name is required`);
      }
      if (!project.projectCode?.trim()) {
        errors.push(`Project ${index + 1}: Code is required`);
      }
      
      // For night shifts, end time CAN be before start time (it's next day)
      if (!project.isNightShift && project.endTime <= project.startTime) {
        errors.push(`Project ${index + 1}: End time must be after start time (enable night shift for cross-midnight)`);
      }
      
      // Validate duration
      if (project.duration > 16) {
        errors.push(`Project ${index + 1}: Duration cannot exceed 16 hours`);
      }
      if (project.duration < 0.5) {
        errors.push(`Project ${index + 1}: Duration must be at least 30 minutes`);
      }
    });

    // Check for time overlaps between projects on same date
    for (let i = 0; i < projects.length; i++) {
      for (let j = i + 1; j < projects.length; j++) {
        const proj1 = projects[i];
        const proj2 = projects[j];

        // Check if same date
        const date1 = proj1.overtimeDate.toDateString();
        const date2 = proj2.overtimeDate.toDateString();

        if (date1 === date2) {
          // Check time overlap
          const start1 = proj1.startTime.getTime();
          const end1 = proj1.endTime.getTime();
          const start2 = proj2.startTime.getTime();
          const end2 = proj2.endTime.getTime();

          const hasOverlap = (start1 < end2 && end1 > start2);

          if (hasOverlap) {
            errors.push(
              `Time overlap detected between "${proj1.projectName}" (${proj1.startTime.toLocaleTimeString()} - ${proj1.endTime.toLocaleTimeString()}) ` +
              `and "${proj2.projectName}" (${proj2.startTime.toLocaleTimeString()} - ${proj2.endTime.toLocaleTimeString()})`
            );
          }
        }
      }
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Create overtime request with multi-project support
   */
  async createOvertimeRequest(requestData: OvertimeRequestData): Promise<OvertimeRequestResult> {
    try {
      console.log('📝 Creating multi-project overtime request...');
  
      // Validate user
      if (!requestData.requesterId || requestData.requesterId === '') {
        throw new Error('Invalid requester ID. Please logout and login again.');
      }
  
      // Validate projects
      const projectValidation = this.validateProjects(requestData.projects);
      if (!projectValidation.valid) {
        throw new Error(projectValidation.errors.join('; '));
      }
  
      // Validate other fields
      if (!requestData.selectedApproverId) {
        throw new Error('Approver selection is required');
      }
      if (requestData.employeeIds.length === 0) {
        throw new Error('At least one employee must be selected');
      }
  
      // Calculate total hours across all projects
      const totalHours = requestData.projects.reduce((sum, proj) => sum + proj.duration, 0);
  
      // Get employee details
      const employeeDetails = await this.getEmployeeDetails(requestData.employeeIds);
  
      // Use first project's date as the primary overtime date
      const primaryOvertimeDate = requestData.projects[0].overtimeDate;
      const validationScheduledFor = this.getNextValidationTime(primaryOvertimeDate);
  
      // Check if any project is a night shift
      const hasAnyNightShift = requestData.projects.some(p => p.isNightShift);
  
      // ✅ FIXED: Create attendance validation object - use null instead of undefined
      const attendanceValidation = {
        isValidated: false,
        validationPending: true,
        validatedAt: null,
        validationResults: requestData.employeeIds.map(empId => {
          const emp = employeeDetails.find(e => e.id === empId);
          return {
            employeeId: empId,
            employeeName: emp?.name || 'Unknown',
            employeePin: emp?.employeeNumber || '',
            hasAttendance: false,
            status: 'pending' as const,
            attendanceRecord: null,  // ✅ FIXED: Use null instead of undefined
            validatedAt: null
          };
        }),
        presentCount: 0,
        absentCount: 0,
        absentEmployeeIds: [],
        absentEmployeeNames: [],
        validationScheduledFor: Timestamp.fromDate(validationScheduledFor)
      };
  
      // Convert projects to Firestore format
      const projectsForFirestore = requestData.projects.map(proj => ({
        id: proj.id,
        projectName: proj.projectName.trim(),
        projectCode: proj.projectCode.trim(),
        startTime: Timestamp.fromDate(proj.startTime),
        endTime: Timestamp.fromDate(proj.endTime),
        duration: proj.duration,
        overtimeDate: Timestamp.fromDate(proj.overtimeDate),
        isNightShift: proj.isNightShift || false,
        hasLongShift: proj.hasLongShift || proj.isNightShift || false
      }));
  
      // ✅ FIXED: Ensure all values are properly initialized (no undefined)
      const overtimeRequestData = {
        // Multi-project structure
        projects: projectsForFirestore,
        totalProjects: requestData.projects.length,
  
        // Legacy fields (use first project for backward compatibility)
        projectName: requestData.projects[0].projectName.trim(),
        projectCode: requestData.projects[0].projectCode.trim(),
        startTime: Timestamp.fromDate(requestData.projects[0].startTime),
        endTime: Timestamp.fromDate(requestData.projects[0].endTime),
        overtimeDate: Timestamp.fromDate(primaryOvertimeDate),
  
        // Night shift flags
        isNightShift: requestData.projects[0].isNightShift || hasAnyNightShift || false,
        hasLongShift: requestData.projects[0].hasLongShift || hasAnyNightShift || false,
  
        // Shared fields
        employeeIds: requestData.employeeIds,
        requesterId: requestData.requesterId,
        requesterName: requestData.requesterName,
        approverEmpId: requestData.selectedApproverId,
        approverName: requestData.selectedApproverName,
        requestTime: serverTimestamp(),
        status: 'pending',
  
        // Enhanced tracking
        totalEmployees: requestData.employeeIds.length,
        totalHours: totalHours,
        isActive: true,
        version: 1,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
  
        // Employee details
        employeeDetails: employeeDetails,
  
        // Attendance validation
        attendanceValidation: attendanceValidation,
        willValidateOn: Timestamp.fromDate(validationScheduledFor),
  
        // Edit tracking
        canEditAfterApproval: true,
        requiresReApproval: false,
        editHistory: [],
  
        // ✅ FIXED: Metadata - ensure all fields have proper values
        metadata: {
          source: 'web_portal',
          userAgent: navigator.userAgent || 'Unknown',
          timestamp: new Date().toISOString(),
          attendanceValidationDeferred: true,
          validationScheduledFor: validationScheduledFor.toISOString(),
          overtimeDateString: primaryOvertimeDate.toISOString().split('T')[0],
          isMultiProject: requestData.projects.length > 1,
          isNightShift: hasAnyNightShift
        },
        approverSelectionSource: 'manual_ui_selection'
      };
  
      // ✅ DEBUG: Log the data to verify no undefined values
      console.log('🔍 Overtime request data before save:', overtimeRequestData);
      console.log('❌ Check for undefined values:', 
        Object.entries(overtimeRequestData).filter(([key, value]) => value === undefined)
      );
  
      console.log('💾 Saving multi-project overtime request...');
      console.log(`📊 Total projects: ${requestData.projects.length}, Total hours: ${totalHours}, Night shift: ${hasAnyNightShift}`);
  
      // Save to Firestore
      const docRef = await addDoc(collection(db, 'overtime_requests'), overtimeRequestData);
  
      console.log('✅ Overtime request created with ID:', docRef.id);
  
      // Send notifications
      await this.sendNotifications({
        requestId: docRef.id,
        approverId: requestData.selectedApproverId,
        approverName: requestData.selectedApproverName,
        requesterId: requestData.requesterId,
        requesterName: requestData.requesterName,
        projects: requestData.projects,
        employeeCount: requestData.employeeIds.length,
        totalHours: totalHours,
        overtimeDate: primaryOvertimeDate,
        validationScheduledFor: validationScheduledFor,
        isNightShift: hasAnyNightShift
      });
  
      // Clear cache
      this.clearCache();
  
      return {
        success: true,
        requestId: docRef.id,
        message: `Overtime request created successfully with ${requestData.projects.length} project(s)${hasAnyNightShift ? ' (Night Shift)' : ''}! Attendance will be validated automatically tomorrow morning.`
      };
  
    } catch (error) {
      console.error('❌ Error creating overtime request:', error);
      
      // ✅ BETTER ERROR HANDLING: Extract the specific field causing the issue
      if (error instanceof FirebaseError && error.message.includes('Unsupported field value: undefined')) {
        console.error('🔍 Undefined field detected. Please check the overtimeRequestData structure.');
      }
      
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to create overtime request'
      };
    }
  }

  /**
   * Get employee details
   */
  private async getEmployeeDetails(employeeIds: string[]): Promise<Array<{
    id: string;
    name: string;
    designation: string;
    department: string;
    employeeNumber: string;
  }>> {
    const employeeDetails: Array<{
      id: string;
      name: string;
      designation: string;
      department: string;
      employeeNumber: string;
    }> = [];

    try {
      const eligibleEmployees = await this.getEligibleEmployees();
      
      for (const empId of employeeIds) {
        const employee = eligibleEmployees.find(emp => emp.id === empId);
        if (employee) {
          employeeDetails.push({
            id: empId,
            name: employee.name,
            designation: employee.designation,
            department: employee.department,
            employeeNumber: employee.employeeNumber || empId
          });
        }
      }
    } catch (error) {
      console.error('Error getting employee details:', error);
    }

    return employeeDetails;
  }

  /**
   * Send notifications with multi-project info
   */
  private async sendNotifications(notificationData: {
    requestId: string;
    approverId: string;
    approverName: string;
    requesterId: string;
    requesterName: string;
    projects: OvertimeProject[];
    employeeCount: number;
    totalHours: number;
    overtimeDate: Date;
    validationScheduledFor: Date;
    isNightShift?: boolean;
  }): Promise<void> {
    try {
      console.log('📧 Sending notifications for multi-project overtime request...');
      
      const notificationsRef = collection(db, 'notifications');
      const dateString = notificationData.overtimeDate.toLocaleDateString();
      const validationTimeString = notificationData.validationScheduledFor.toLocaleString();
      
      // Create project summary
      const projectSummary = notificationData.projects.length === 1
        ? notificationData.projects[0].projectName
        : `${notificationData.projects.length} projects`;

      const nightShiftLabel = notificationData.isNightShift ? ' (Night Shift)' : '';

      // Notification for approver
      await addDoc(notificationsRef, {
        type: 'overtime_request',
        targetUserId: notificationData.approverId,
        title: `New Overtime Request${nightShiftLabel}`,
        body: `${notificationData.requesterName} requested overtime for ${notificationData.employeeCount} employees across ${projectSummary} on ${dateString}. Total: ${notificationData.totalHours.toFixed(1)} hours.`,
        data: {
          requestId: notificationData.requestId,
          projectCount: notificationData.projects.length.toString(),
          requesterName: notificationData.requesterName,
          employeeCount: notificationData.employeeCount.toString(),
          totalHours: notificationData.totalHours.toString(),
          overtimeDate: dateString,
          validationScheduledFor: validationTimeString,
          isNightShift: (notificationData.isNightShift || false).toString()
        },
        timestamp: serverTimestamp(),
        processed: false
      });

      // Confirmation for requester
      await addDoc(notificationsRef, {
        type: 'overtime_request_submitted',
        targetUserId: notificationData.requesterId,
        title: `Overtime Request Submitted${nightShiftLabel}`,
        body: `Your overtime request for ${projectSummary} has been sent to ${notificationData.approverName}. Attendance will be validated on ${validationTimeString}.`,
        data: {
          requestId: notificationData.requestId,
          projectCount: notificationData.projects.length.toString(),
          approverName: notificationData.approverName,
          overtimeDate: dateString,
          validationScheduledFor: validationTimeString,
          isNightShift: (notificationData.isNightShift || false).toString()
        },
        timestamp: serverTimestamp(),
        processed: false
      });

      console.log('✅ Notifications sent successfully');
    } catch (error) {
      console.error('❌ Error sending notifications:', error);
    }
  }

  /**
   * Clear cache
   */
  clearCache(): void {
    this.cache.clear();
    console.log('Create overtime service cache cleared');
  }

  /**
   * Test service connection
   */
  async testConnection(): Promise<{ success: boolean; message: string }> {
    try {
      const approvers = await this.getAvailableApprovers();
      const employees = await this.getEligibleEmployees();
      
      return {
        success: true,
        message: `Service connected. Found ${approvers.length} approvers and ${employees.length} employees.`
      };
    } catch (error) {
      return {
        success: false,
        message: `Connection failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }
}

export const createOvertimeService = new CreateOvertimeService();

